//
// Copyright (c) 2024 Emplifi, Inc.
//

import { type Editor, type ModelItem, Plugin, toWidget, type ViewDowncastWriter } from "ckeditor5";
import SnippetCommand from "../snippet/snippetCommand";
import { PLACEHOLDER_VALUE } from "../ckEditorConstants";

export default class SnippetEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [SnippetCommand];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "SnippetEditing";
  }

  init() {
    const editor = this.editor;
    this._setupConfig(editor);
    this._setupSchema(editor);
    this._setupConversions(editor);
    editor.commands.add("snippet", new SnippetCommand(editor));
  }

  _createViewElement(modelItem: ModelItem, writer: ViewDowncastWriter, allowPlaceholder = false) {
    const snippetId = modelItem.getAttribute("snippetId");
    if (snippetId == PLACEHOLDER_VALUE && !allowPlaceholder) {
      return null;
    }
    const snippetName: string =
      (modelItem.getAttribute("snippetName") as string) ||
      (this.editor.config.get("snippet.emptyName") as string);
    const element = writer.createContainerElement("div", {
      "data-snippet-id": snippetId,
    });
    const text = writer.createText(snippetName);
    writer.insert(writer.createPositionAt(element, 0), text);
    return element;
  }

  _setupConfig(editor: Editor) {
    editor.config.define("snippet", {
      blockedId: 0,
      emptyName: "Empty",
      skipOpenSnippetEditor: false,
      toolbarName: "Snippet Toolbar",
    });
  }

  _setupConversions(editor: Editor) {
    const conversion = editor.conversion;
    conversion.for("upcast").elementToElement({
      view: {
        name: "div",
        attributes: ["data-snippet-id"],
      },
      model: (viewElement, { writer }) => {
        const snippetId = viewElement.getAttribute("data-snippet-id");
        // @ts-ignore
        const snippetName = viewElement.getChild(0)?.data;
        return writer.createElement("snippet", {
          snippetId,
          snippetName,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "snippet",
      view: (modelItem, { writer }) => this._createViewElement(modelItem, writer),
    });
    conversion.for("editingDowncast").elementToElement({
      model: "snippet",
      view: (modelItem, { writer }) => {
        const element = this._createViewElement(modelItem, writer, true);
        if (!element) {
          return null;
        }
        return toWidget(element, writer);
      },
    });
  }

  _setupSchema(editor: Editor) {
    editor.model.schema.register("snippet", {
      allowWhere: "$blockObject",
      inheritTypesFrom: "$blockObject",
      isObject: true,
      allowAttributes: ["snippetId", "snippetName"],
    });
  }
}
